import { Dispatch, RefObject, SetStateAction, useRef, useState } from "react";
import { SsqPostAttachment } from "src/api/client-api/post/postSchema";
import {
    downloadAttachmentFile,
    uploadAttachmentFile,
    useDeleteAttachment,
} from "src/api/client-api/property/useSaveComment";
import { format, parseISO } from "date-fns";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "src/utils/useSnackbar";
import { sanitizeFilename } from "../utils/sanitizeFilename";
import { getDisplayFilename, getStorageFilenameFromPath } from "../utils/attachmentFilename";
import { MAX_FILE_SIZE_MB, formatFileSizeMb, isFileTooLarge, isSupportedFormat } from "./attachmentLimits";

export type Attachment = {
    id: string;
    filename: string;
    displayName: string;
    createdAt: string;
};

type Options = {
    changePost: () => void;
};

export const usePropertyAttachment = (options: Options): AttachmentState => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [openUploadDialog, setOpenUploadDialog] = useState<boolean>(false);
    const [openAttachmentDialog, setOpenAttachmentDialog] = useState<boolean>(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const [ssqPostElementId, setSsqPostElementId] = useState<number | null>(null);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const deleteAttachment = useDeleteAttachment();

    const [temporaryFiles, setTemporaryFiles] = useState<File[]>([]);

    const validateFile = (file: File): string[] => {
        const errors: string[] = [];

        if (!isSupportedFormat(file.name)) {
            errors.push(t("property.attachment.not_supported_format", "Format not supported"));
        }

        if (isFileTooLarge(file.size)) {
            errors.push(
                t(
                    "property.attachment.oversized",
                    "This file is too large. Maximum size is {{maxSize}} MB (your file is {{fileSize}} MB).",
                    { maxSize: MAX_FILE_SIZE_MB, fileSize: formatFileSizeMb(file.size) },
                ),
            );
        }

        return errors;
    };

    const addTemporaryFiles = (files: FileList | File[]) => {
        const fileArray = Array.from(files);
        if (fileArray.length === 0) {
            return;
        }

        const errors: string[] = [];
        const validFiles: File[] = [];

        for (const file of fileArray) {
            const fileErrors = validateFile(file);
            if (fileErrors.length > 0) {
                errors.push(...fileErrors);
            } else {
                validFiles.push(file);
            }
        }

        if (errors.length > 0) {
            setErrorMessages(errors);
        } else {
            setErrorMessages([]);
        }

        if (validFiles.length > 0) {
            setTemporaryFiles((current) => [...current, ...validFiles]);
        }
    };

    const removeTemporaryFile = (index: number) => {
        setTemporaryFiles((current) => current.filter((_, fileIndex) => fileIndex !== index));
        setErrorMessages([]);
    };

    const clearTemporaryFiles = () => {
        setTemporaryFiles([]);
    };

    const [isUploading, setIsUploading] = useState(false);
    const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
    const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<string | null>(null);

    const [errorMessages, setErrorMessages] = useState<string[]>([]);

    const handleOpenUploadDialog = (ssqPostElementId: number, ssqPostAttachment?: SsqPostAttachment[]) => {
        setSsqPostElementId(ssqPostElementId);
        setAttachments(formatSsqPostAttachments(ssqPostAttachment));
        setOpenUploadDialog(true);
    };

    const closeDialog = () => {
        queryClient.cancelMutations();
        setOpenUploadDialog(false);
        setOpenAttachmentDialog(false);
        clearTemporaryFiles();
        setAttachments([]);
        setErrorMessages([]);
        setIsUploading(false);
        setDeletingAttachmentId(null);
        setDownloadingAttachmentId(null);
    };

    const deleteRemoteFile = (attachment: Attachment) => {
        if (ssqPostElementId === null) return;

        setDeletingAttachmentId(attachment.id);
        deleteAttachment.mutate(
            {
                ssqPostElementId,
                filename: attachment.filename,
                attachmentId: attachment.id,
            },
            {
                onSuccess: () => {
                    setAttachments((current) => current.filter((item) => item.id !== attachment.id));
                    options.changePost();
                },
                onSettled: () => {
                    setDeletingAttachmentId(null);
                    setErrorMessages([]);
                },
            },
        );
    };

    const downloadRemoteFile = async (attachment: Attachment) => {
        if (ssqPostElementId === null) return;

        setDownloadingAttachmentId(attachment.id);
        try {
            const file = await downloadAttachmentFile(ssqPostElementId, attachment.filename);
            const fileURL = URL.createObjectURL(file);
            const anchor = document.createElement("a");
            anchor.href = fileURL;
            anchor.download = attachment.displayName;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(fileURL);
        } finally {
            setDownloadingAttachmentId(null);
        }
    };

    const handleSaveClose = async () => {
        if (temporaryFiles.length === 0) return;
        if (ssqPostElementId === null) return;

        setIsUploading(true);
        setErrorMessages([]);

        const uploadedAttachments: Attachment[] = [];

        try {
            for (const file of temporaryFiles) {
                const sanitizedFilename = sanitizeFilename(file.name);
                const sanitizedFile = new File([file], sanitizedFilename, { type: file.type });
                const createdAttachment = await uploadAttachmentFile({ ssqPostElementId, file: sanitizedFile });
                uploadedAttachments.push({
                    id: createdAttachment.id,
                    filename: createdAttachment.filename,
                    displayName: createdAttachment.displayName,
                    createdAt: format(new Date(), "dd/MM/yyyy, HH:mm"),
                });
            }

            if (uploadedAttachments.length > 0) {
                setAttachments((current) => [...current, ...uploadedAttachments]);
                options.changePost();
                snackbar.setSnackbarProps({
                    message: t("property.attachment.upload_success", "Attached file uploaded successfully"),
                    type: "success",
                });
            }

            clearTemporaryFiles();
        } catch (error) {
            console.error("S3 file upload failed : ", error);
            snackbar.setSnackbarProps({
                type: "error",
                message: t("property.attachment.upload_error", "An error occurred while uploading the file"),
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleCancel = () => {
        closeDialog();
    };

    const hasPendingFiles = temporaryFiles.length > 0;

    return {
        isUploadAttachmentDialogOpen: openUploadDialog,
        isViewAttachmentDialogOpen: openAttachmentDialog,
        ssqPostElementId,

        fileInputRef,
        triggerFileInput,

        isUploading,
        attachments,
        deletingAttachmentId,
        downloadingAttachmentId,

        temporaryFiles,
        hasPendingFiles,
        addTemporaryFiles,
        removeTemporaryFile,
        saveSelectedFile: handleSaveClose,

        errorMessages,
        setErrorMessages,

        deleteRemoteFile,
        downloadRemoteFile,

        handleCancel,

        setOpenUploadDialog,
        openAttachmentDialog: handleOpenUploadDialog,
        closeDialog,
    };
};

export type AttachmentState = {
    isUploadAttachmentDialogOpen: boolean;
    isViewAttachmentDialogOpen: boolean;

    fileInputRef: RefObject<HTMLInputElement | null>;
    triggerFileInput: () => void;

    errorMessages: string[];
    setErrorMessages: (mess: string[]) => void;

    ssqPostElementId: number | null;
    attachments: Attachment[];
    deletingAttachmentId: string | null;
    downloadingAttachmentId: string | null;

    temporaryFiles: File[];
    hasPendingFiles: boolean;
    isUploading: boolean;
    saveSelectedFile: () => void;
    addTemporaryFiles: (files: FileList | File[]) => void;
    removeTemporaryFile: (index: number) => void;

    deleteRemoteFile: (attachment: Attachment) => void;
    downloadRemoteFile: (attachment: Attachment) => Promise<void>;

    handleCancel: () => void;

    closeDialog: () => void;
    setOpenUploadDialog: Dispatch<SetStateAction<boolean>>;
    openAttachmentDialog: (ssqPostElementId: number, uploadFileData?: SsqPostAttachment[]) => void;
};

const formatSsqPostAttachments = (ssqPostAttachments?: SsqPostAttachment[]): Attachment[] =>
    (ssqPostAttachments ?? []).map((attachment) => {
        const filename = getStorageFilenameFromPath(attachment.attachmentPath);
        const updatedAtDate = parseISO(attachment.createdAt);

        return {
            id: attachment.id,
            filename,
            displayName: getDisplayFilename(attachment.id, attachment.attachmentPath),
            createdAt: format(updatedAtDate, "dd/MM/yyyy, HH:mm"),
        };
    });
